import React, { useEffect, useState } from "react";

import Navbar from "../components/Navbar";
import SecondNavbar from "../components/SecondNavbar";
import Footer from "../components/Footer";

import "./MyReports.css";

import {
  getPublishedVolunteerResponses,
  getPublicResolvedReports,
  deleteVolunteerResponse,
} from "../services/reportService";

/* =========================================================
   ADMIN CHECK
   ---------------------------------------------------------
   Checks:
   1. localStorage role
   2. localStorage user.role
   3. localStorage user.roles[]
========================================================= */

const checkIsAdmin = () => {
  /* -------------------------------------------------------
     CHECK DIRECT ROLE
  ------------------------------------------------------- */

  const directRole = localStorage.getItem("role");

  if (
    directRole &&
    String(directRole).toLowerCase() === "admin"
  ) {
    return true;
  }

  /* -------------------------------------------------------
     CHECK USER OBJECT
  ------------------------------------------------------- */

  try {
    const storedUser = JSON.parse(
      localStorage.getItem("user") || "{}"
    );

    /* user.role */

    if (
      String(storedUser?.role || "").toLowerCase() ===
      "admin"
    ) {
      return true;
    }

    /* user.roles */

    if (Array.isArray(storedUser?.roles)) {
      const hasAdminRole = storedUser.roles.some(
        (role) =>
          String(role).toLowerCase() === "admin"
      );

      if (hasAdminRole) {
        return true;
      }
    }
  } catch (error) {
    console.error(
      "User localStorage parse error:",
      error
    );
  }

  /* -------------------------------------------------------
     EXTRA CHECK:
     Sometimes role may be stored as JSON array/string
  ------------------------------------------------------- */

  try {
    if (directRole) {
      const parsedRole = JSON.parse(directRole);

      if (Array.isArray(parsedRole)) {
        return parsedRole.some(
          (role) =>
            String(role).toLowerCase() === "admin"
        );
      }

      if (
        String(parsedRole).toLowerCase() === "admin"
      ) {
        return true;
      }
    }
  } catch (error) {
    // Raw role such as "admin" is already checked above.
  }

  return false;
};


/* =========================================================
   IMAGE URL
========================================================= */

const getImageUrl = (image) => {
  if (!image) {
    return "";
  }

  /* If backend already sends full URL */

  if (
    String(image).startsWith("http://") ||
    String(image).startsWith("https://")
  ) {
    return image;
  }

  /* Remove starting slash */

  const cleanImage = String(image).replace(
    /^\/+/,
    ""
  );

  return `/${cleanImage}`;
};


/* =========================================================
   DATE FORMAT
========================================================= */

const formatDate = (date) => {
  if (!date) {
    return "N/A";
  }

  try {
    return new Date(date).toLocaleDateString(
      "en-US",
      {
        year: "numeric",
        month: "long",
        day: "numeric",
      }
    );
  } catch (error) {
    return "N/A";
  }
};


/* =========================================================
   VOLUNTEER RESPONSE CARD
========================================================= */

const VolunteerResponseCard = ({
  item,
  isAdminUser,
  deletingVolunteerId,
  onDelete,
}) => {
  /* -------------------------------------------------------
     TASK ID
  ------------------------------------------------------- */

  const taskId =
    item?._id ||
    item?.id ||
    item?.taskId;

  /* -------------------------------------------------------
     REPORT
  ------------------------------------------------------- */

  const report =
    item?.report ||
    item?.reportId ||
    {};

  /* -------------------------------------------------------
     VOLUNTEER
  ------------------------------------------------------- */

  const volunteer =
    item?.volunteer ||
    item?.assignedVolunteer ||
    item?.volunteerId ||
    {};

  /* -------------------------------------------------------
     BASIC DATA
  ------------------------------------------------------- */

  const activity =
    item?.activity ||
    item?.activityTitle ||
    item?.taskTitle ||
    "Volunteer Activity";

  const description =
    item?.description ||
    item?.volunteerDescription ||
    item?.workDescription ||
    "Volunteer work completed successfully.";

  const solution =
    item?.solution ||
    item?.response ||
    item?.workDone ||
    item?.resolution ||
    "";

  const notes =
    item?.notes ||
    item?.volunteerNotes ||
    "";

  const progress =
    item?.progress ||
    item?.completionPercentage ||
    item?.completion ||
    "";

  const status =
    item?.adminReviewStatus ||
    item?.status ||
    "Approved";

  const location =
    report?.location ||
    item?.location ||
    "N/A";

  const category =
    report?.category ||
    item?.category ||
    "Environmental";

  const priority =
    report?.priority ||
    item?.priority ||
    "N/A";

  const publishedDate =
    item?.publishedAt ||
    item?.updatedAt ||
    item?.createdAt;

  /* -------------------------------------------------------
     EVIDENCE IMAGE
  ------------------------------------------------------- */

  const evidenceImage =
    item?.workEvidence ||
    item?.evidence ||
    item?.proof ||
    item?.image ||
    item?.photo ||
    "";

  /* -------------------------------------------------------
     ORIGINAL REPORT TITLE
  ------------------------------------------------------- */

  const reportTitle =
    report?.title ||
    report?.description ||
    item?.reportTitle ||
    "Environmental Report";

  /* -------------------------------------------------------
     VOLUNTEER NAME
  ------------------------------------------------------- */

  const volunteerName =
    volunteer?.name ||
    volunteer?.fullName ||
    volunteer?.username ||
    item?.volunteerName ||
    "EcoVibe Volunteer";

  /* -------------------------------------------------------
     DELETE LOADING
  ------------------------------------------------------- */

  const isDeleting =
    String(deletingVolunteerId) ===
    String(taskId);

  return (
    <div className="report-card volunteer-response-card">

      {/* =====================================================
          CARD HEADER
      ===================================================== */}

      <div className="report-card-header">

        <div>
          <span className="report-card-label">
            Volunteer Work Published
          </span>

          <h3>
            {activity}
          </h3>
        </div>

        <span className="report-status solved">
          Solved
        </span>

      </div>


      {/* =====================================================
          VOLUNTEER INFORMATION
      ===================================================== */}

      <div className="report-info-grid">

        <div className="report-info-item">
          <span className="report-info-label">
            Solved By
          </span>

          <strong>
            {volunteerName}
          </strong>
        </div>


        <div className="report-info-item">
          <span className="report-info-label">
            Activity
          </span>

          <strong>
            {activity}
          </strong>
        </div>


        <div className="report-info-item">
          <span className="report-info-label">
            Status
          </span>

          <strong>
            {status}
          </strong>
        </div>


        <div className="report-info-item">
          <span className="report-info-label">
            Location
          </span>

          <strong>
            {location}
          </strong>
        </div>


        <div className="report-info-item">
          <span className="report-info-label">
            Category
          </span>

          <strong>
            {category}
          </strong>
        </div>


        <div className="report-info-item">
          <span className="report-info-label">
            Priority
          </span>

          <strong>
            {priority}
          </strong>
        </div>


        <div className="report-info-item">
          <span className="report-info-label">
            Published Date
          </span>

          <strong>
            {formatDate(publishedDate)}
          </strong>
        </div>


        <div className="report-info-item">
          <span className="report-info-label">
            Admin Approval
          </span>

          <strong className="approved-text">
            Approved
          </strong>
        </div>

      </div>


      {/* =====================================================
          VOLUNTEER DESCRIPTION
      ===================================================== */}

      {description && (
        <div className="report-content-section">

          <h4>
            Volunteer Description
          </h4>

          <p>
            {description}
          </p>

        </div>
      )}


      {/* =====================================================
          SOLUTION / WORK DONE
      ===================================================== */}

      {solution && (
        <div className="report-content-section">

          <h4>
            Work Done / Solution
          </h4>

          <p>
            {solution}
          </p>

        </div>
      )}


      {/* =====================================================
          NOTES
      ===================================================== */}

      {notes && (
        <div className="report-content-section">

          <h4>
            Volunteer Notes
          </h4>

          <p>
            {notes}
          </p>

        </div>
      )}


      {/* =====================================================
          PROGRESS
      ===================================================== */}

      {progress !== "" &&
        progress !== null &&
        progress !== undefined && (
          <div className="report-content-section">

            <h4>
              Completion Progress
            </h4>

            <p>
              {progress}
              {typeof progress === "number"
                ? "%"
                : ""}
            </p>

          </div>
        )}


      {/* =====================================================
          WORK EVIDENCE
      ===================================================== */}

      {evidenceImage && (
        <div className="report-evidence-section">

          <h4>
            Volunteer Work Evidence
          </h4>

          <div className="report-evidence-image-wrapper">

            <img
              src={getImageUrl(evidenceImage)}
              alt="Volunteer work evidence"
              className="report-evidence-image"
              onError={(event) => {
                event.currentTarget.style.display =
                  "none";
              }}
            />

          </div>

        </div>
      )}


      {/* =====================================================
          ORIGINAL ENVIRONMENTAL REPORT
      ===================================================== */}

      <div className="original-report-section">

        <h4>
          Original Environmental Report
        </h4>

        <p>
          {reportTitle}
        </p>

      </div>


      {/* =====================================================
          ADMIN DELETE BUTTON
          -----------------------------------------------------
          IMPORTANT:
          This deletes ONLY VolunteerTask.
          Original Report remains safe.
      ===================================================== */}

      {isAdminUser && (
        <div className="volunteer-delete-section">

          <button
            type="button"
            className="delete-volunteer-response-btn"
            onClick={() =>
              onDelete(taskId)
            }
            disabled={
              isDeleting ||
              !taskId
            }
          >
            {isDeleting
              ? "Deleting..."
              : "Delete Volunteer Work"}
          </button>

        </div>
      )}

    </div>
  );
};


/* =========================================================
   NGO RESOLUTION CARD
========================================================= */

const NGOResolutionCard = ({ item }) => {

  const report =
    item?.report ||
    item?.reportId ||
    {};

  const ngo =
    item?.ngo ||
    item?.ngoId ||
    item?.organization ||
    {};

  const title =
    report?.title ||
    report?.description ||
    item?.title ||
    "NGO Resolution";

  const description =
    item?.description ||
    item?.resolution ||
    item?.response ||
    item?.solution ||
    "Environmental issue resolved by NGO.";

  const location =
    report?.location ||
    item?.location ||
    "N/A";

  const category =
    report?.category ||
    item?.category ||
    "Environmental";

  const date =
    item?.resolvedAt ||
    item?.updatedAt ||
    item?.createdAt;

  const ngoName =
    ngo?.name ||
    ngo?.organizationName ||
    item?.ngoName ||
    "Environmental NGO";

  const image =
    item?.image ||
    item?.proof ||
    item?.evidence ||
    item?.resolutionImage ||
    "";

  return (
    <div className="report-card ngo-resolution-card">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="report-card-header">

        <div>

          <span className="report-card-label">
            NGO Resolution
          </span>

          <h3>
            {title}
          </h3>

        </div>

        <span className="report-status solved">
          Resolved
        </span>

      </div>


      {/* =====================================================
          NGO INFO
      ===================================================== */}

      <div className="report-info-grid">

        <div className="report-info-item">

          <span className="report-info-label">
            Resolved By
          </span>

          <strong>
            {ngoName}
          </strong>

        </div>


        <div className="report-info-item">

          <span className="report-info-label">
            Location
          </span>

          <strong>
            {location}
          </strong>

        </div>


        <div className="report-info-item">

          <span className="report-info-label">
            Category
          </span>

          <strong>
            {category}
          </strong>

        </div>


        <div className="report-info-item">

          <span className="report-info-label">
            Resolution Date
          </span>

          <strong>
            {formatDate(date)}
          </strong>

        </div>

      </div>


      {/* =====================================================
          DESCRIPTION
      ===================================================== */}

      <div className="report-content-section">

        <h4>
          Resolution Details
        </h4>

        <p>
          {description}
        </p>

      </div>


      {/* =====================================================
          IMAGE
      ===================================================== */}

      {image && (
        <div className="report-evidence-section">

          <h4>
            Resolution Evidence
          </h4>

          <div className="report-evidence-image-wrapper">

            <img
              src={getImageUrl(image)}
              alt="NGO resolution evidence"
              className="report-evidence-image"
              onError={(event) => {
                event.currentTarget.style.display =
                  "none";
              }}
            />

          </div>

        </div>
      )}

    </div>
  );
};


/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function MyReports() {

  /* =======================================================
     STATE
  ======================================================= */

  const [
    publishedVolunteerResponses,
    setPublishedVolunteerResponses,
  ] = useState([]);

  const [
    publishedNGOResolutions,
    setPublishedNGOResolutions,
  ] = useState([]);

  const [
    loadingVolunteer,
    setLoadingVolunteer,
  ] = useState(true);

  const [
    loadingNGO,
    setLoadingNGO,
  ] = useState(true);

  const [
    errorVolunteer,
    setErrorVolunteer,
  ] = useState("");

  const [
    errorNGO,
    setErrorNGO,
  ] = useState("");

  const [
    deletingVolunteerId,
    setDeletingVolunteerId,
  ] = useState(null);


  /* =======================================================
     ADMIN STATUS
  ======================================================= */

  const [
    isAdminUser,
    setIsAdminUser,
  ] = useState(false);


  /* =======================================================
     CHECK ADMIN ON PAGE LOAD
  ======================================================= */

  useEffect(() => {

    const adminStatus =
      checkIsAdmin();

    console.log(
      "======================================"
    );

    console.log(
      "MY REPORTS ADMIN CHECK"
    );

    console.log(
      "localStorage role:",
      localStorage.getItem("role")
    );

    console.log(
      "localStorage user:",
      localStorage.getItem("user")
    );

    console.log(
      "Is Admin:",
      adminStatus
    );

    console.log(
      "======================================"
    );

    setIsAdminUser(adminStatus);

  }, []);


  /* =======================================================
     FETCH PUBLISHED DATA
  ======================================================= */

  useEffect(() => {

    fetchPublishedVolunteerResponses();

    fetchPublishedNGOResolutions();

  }, []);


  /* =======================================================
     FETCH VOLUNTEER WORK
  ======================================================= */

  const fetchPublishedVolunteerResponses =
    async () => {

      try {

        setLoadingVolunteer(true);

        setErrorVolunteer("");

        const response =
          await getPublishedVolunteerResponses();

        console.log(
          "========== MY REPORTS VOLUNTEER =========="
        );

        console.log(
          response
        );

        console.log(
          "=========================================="
        );

        /*
         * Backend returns:
         *
         * {
         *   success: true,
         *   count: ...,
         *   data: [...]
         * }
         */

        const data =
          Array.isArray(response)
            ? response
            : response?.data || [];

        setPublishedVolunteerResponses(
          data
        );

      } catch (error) {

        console.error(
          "Fetch Published Volunteer Responses Error:",
          error
        );

        setErrorVolunteer(
          error?.response?.data?.message ||
          error?.message ||
          "Unable to load published volunteer work."
        );

      } finally {

        setLoadingVolunteer(false);

      }

    };


  /* =======================================================
     FETCH NGO RESOLUTIONS
  ======================================================= */

  const fetchPublishedNGOResolutions =
    async () => {

      try {

        setLoadingNGO(true);

        setErrorNGO("");

        const response =
          await getPublicResolvedReports();

        console.log(
          "========== MY REPORTS NGO =========="
        );

        console.log(
          response
        );

        console.log(
          "===================================="
        );

        const data =
          Array.isArray(response)
            ? response
            : response?.data || [];

        setPublishedNGOResolutions(
          data
        );

      } catch (error) {

        console.error(
          "Fetch Published NGO Resolutions Error:",
          error
        );

        setErrorNGO(
          error?.response?.data?.message ||
          error?.message ||
          "Unable to load NGO resolutions."
        );

      } finally {

        setLoadingNGO(false);

      }

    };


  /* =======================================================
     DELETE VOLUNTEER WORK
     -------------------------------------------------------
     IMPORTANT:
     This calls:
     DELETE /reports/volunteer-tasks/:id
     
     It deletes ONLY VolunteerTask.
     It DOES NOT delete original Report.
  ======================================================= */

  const handleDeleteVolunteerResponse =
    async (taskId) => {

      /* ---------------------------------------------------
         ID CHECK
      --------------------------------------------------- */

      if (!taskId) {

        alert(
          "Volunteer response ID not found."
        );

        return;
      }


      /* ---------------------------------------------------
         ADMIN CHECK
      --------------------------------------------------- */

      if (!checkIsAdmin()) {

        alert(
          "Only admin can delete volunteer work."
        );

        return;
      }


      /* ---------------------------------------------------
         CONFIRMATION
      --------------------------------------------------- */

      const confirmed =
        window.confirm(
          "Are you sure you want to delete this volunteer work from the website?"
        );

      if (!confirmed) {
        return;
      }


      try {

        setDeletingVolunteerId(
          taskId
        );


        console.log(
          "=========================================="
        );

        console.log(
          "DELETE VOLUNTEER WORK"
        );

        console.log(
          "Task ID:",
          taskId
        );

        console.log(
          "=========================================="
        );


        /* -------------------------------------------------
           DELETE API
        ------------------------------------------------- */

        await deleteVolunteerResponse(
          taskId
        );


        /* -------------------------------------------------
           REMOVE CARD FROM UI
        ------------------------------------------------- */

        setPublishedVolunteerResponses(
          (previousItems) =>
            previousItems.filter(
              (item) =>
                String(
                  item?._id ||
                  item?.id ||
                  item?.taskId
                ) !==
                String(taskId)
            )
        );


        alert(
          "Volunteer work deleted successfully."
        );

      } catch (error) {

        console.error(
          "Delete Volunteer Response Error:",
          error
        );


        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to delete volunteer work.";


        alert(message);

      } finally {

        setDeletingVolunteerId(
          null
        );

      }

    };


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <>
      <Navbar />

      <SecondNavbar />


      <main className="my-reports-page">

        {/* =================================================
            PAGE HEADER
        ================================================= */}

        <section className="my-reports-header">

          <div className="my-reports-header-content">

            <span className="my-reports-eyebrow">
              EcoVibe Community Impact
            </span>

            <h1>
              Environmental Reports
            </h1>

            <p>
              Explore environmental issues resolved
              through volunteers and environmental
              organizations.
            </p>

          </div>

        </section>


        {/* =================================================
            CONTENT
        ================================================= */}

        <section className="my-reports-container">

          <div className="my-reports-columns">


            {/* =============================================
                NGO RESOLUTIONS
            ============================================= */}

            <div className="my-reports-column">

              <div className="section-heading">

                <div>

                  <span className="section-kicker">
                    Community Partners
                  </span>

                  <h2>
                    NGO Resolutions
                  </h2>

                </div>

                <span className="section-count">
                  {publishedNGOResolutions.length}
                </span>

              </div>


              {/* LOADING */}

              {loadingNGO && (
                <div className="reports-message loading-message">

                  <div className="loading-spinner"></div>

                  <p>
                    Loading NGO resolutions...
                  </p>

                </div>
              )}


              {/* ERROR */}

              {!loadingNGO &&
                errorNGO && (
                  <div className="reports-message error-message">

                    <p>
                      {errorNGO}
                    </p>

                  </div>
                )}


              {/* EMPTY */}

              {!loadingNGO &&
                !errorNGO &&
                publishedNGOResolutions.length ===
                  0 && (
                  <div className="reports-message empty-message">

                    <p>
                      No NGO resolutions have been
                      published yet.
                    </p>

                  </div>
                )}


              {/* CARDS */}

              {!loadingNGO &&
                !errorNGO &&
                publishedNGOResolutions.length >
                  0 && (

                  <div className="reports-list">

                    {publishedNGOResolutions.map(
                      (item, index) => (

                        <NGOResolutionCard
                          key={
                            item?._id ||
                            item?.id ||
                            index
                          }
                          item={item}
                        />

                      )
                    )}

                  </div>

                )}

            </div>


            {/* =============================================
                VOLUNTEER WORK
            ============================================= */}

            <div className="my-reports-column">

              <div className="section-heading">

                <div>

                  <span className="section-kicker">
                    EcoVibe Volunteers
                  </span>

                  <h2>
                    Volunteer Work
                  </h2>

                </div>

                <span className="section-count">
                  {publishedVolunteerResponses.length}
                </span>

              </div>


              {/* LOADING */}

              {loadingVolunteer && (
                <div className="reports-message loading-message">

                  <div className="loading-spinner"></div>

                  <p>
                    Loading volunteer work...
                  </p>

                </div>
              )}


              {/* ERROR */}

              {!loadingVolunteer &&
                errorVolunteer && (
                  <div className="reports-message error-message">

                    <p>
                      {errorVolunteer}
                    </p>

                  </div>
                )}


              {/* EMPTY */}

              {!loadingVolunteer &&
                !errorVolunteer &&
                publishedVolunteerResponses.length ===
                  0 && (
                  <div className="reports-message empty-message">

                    <p>
                      No volunteer work has been
                      published yet.
                    </p>

                  </div>
                )}


              {/* CARDS */}

              {!loadingVolunteer &&
                !errorVolunteer &&
                publishedVolunteerResponses.length >
                  0 && (

                  <div className="reports-list">

                    {publishedVolunteerResponses.map(
                      (item, index) => (

                        <VolunteerResponseCard
                          key={
                            item?._id ||
                            item?.id ||
                            item?.taskId ||
                            index
                          }
                          item={item}
                          isAdminUser={
                            isAdminUser
                          }
                          deletingVolunteerId={
                            deletingVolunteerId
                          }
                          onDelete={
                            handleDeleteVolunteerResponse
                          }
                        />

                      )
                    )}

                  </div>

                )}

            </div>

          </div>

        </section>

      </main>


      <Footer />
    </>
  );
}